import { Slider } from "radix-ui"
import { useAtomValue } from "jotai"
import { ComponentProps } from "react"
import {
  beginAtom,
  currentDurationAtom,
  currentTimeAtom,
  endAtom,
} from "app/player/state"
import { cn } from "app/lib/utils"
import { useCompactNavigation } from "web/src/slides/hooks/useCompactNavigation"

type Props = {
  bufferedAmount?: TimeRanges
  className?: string
  onScrubStart?: () => void
  onScrubEnd?: () => void
  compact?: boolean
} & Omit<ComponentProps<typeof Slider.Root>, "value" | "min" | "max">

export default function PlayerProgressBar({
  bufferedAmount,
  className,
  onValueChange,
  onScrubStart,
  onScrubEnd,
  onPointerDown,
  onPointerUp,
  onLostPointerCapture,
  onValueCommit,
  compact,
  ...props
}: Props) {
  const isViewportCompact = useCompactNavigation()
  const isCompact = compact ?? isViewportCompact
  const begin = useAtomValue(beginAtom)
  const end = useAtomValue(endAtom)
  const currentDuration = useAtomValue(currentDurationAtom)
  const currentTime = useAtomValue(currentTimeAtom)

  const bufferedPercentage = () => {
    if (!bufferedAmount || !currentDuration) return undefined
    return (
      (bufferedAmount.end(bufferedAmount.length - 1) / currentDuration) * 100
    )
  }

  const bufferPercent = bufferedAmount?.length
    ? bufferedPercentage()
    : undefined

  return (
    <div className={cn("relative w-full", !isCompact && "py-3")}>
      <Slider.Root
        className={cn(
          "relative flex w-full touch-none items-center select-none",
          className
        )}
        min={begin || 0}
        max={end || currentDuration || 0}
        value={[currentTime]}
        onValueChange={onValueChange}
        onPointerDown={(event) => {
          onScrubStart?.()
          onPointerDown?.(event)
        }}
        onPointerUp={(event) => {
          onScrubEnd?.()
          onPointerUp?.(event)
        }}
        onLostPointerCapture={(event) => {
          onScrubEnd?.()
          onLostPointerCapture?.(event)
        }}
        onValueCommit={(value) => {
          onScrubEnd?.()
          onValueCommit?.(value)
        }}
        {...props}
      >
        <Slider.Track
          className="relative h-2.5 w-full grow rounded-full border-[1px] border-black bg-white/30"
          style={
            bufferPercent !== undefined
              ? {
                  background: `linear-gradient(90deg, rgba(255,255,255,0.5) ${bufferPercent}%, rgba(255,255,255,0.3) ${bufferPercent}%)`,
                }
              : undefined
          }
        >
          <Slider.Range className="absolute h-full rounded-full bg-gradient-to-r from-actie-500 to-actie-600" />
        </Slider.Track>
        <Slider.Thumb
          className={cn(
            "block rounded-full focus:outline-none",
            isCompact
              ? "size-8 bg-white shadow-[0_3px_6px_rgba(0,0,0,1)]"
              : "size-[26px] border border-black bg-gray-25 shadow-[0_0_8px_rgba(0,0,0,0.25)]"
          )}
        />
      </Slider.Root>
    </div>
  )
}
